import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Menu, X, ArrowRight, Play } from "lucide-react";
import BrandText from "../BrandText";

const navLinks = [
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "/request-demo" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="landing-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[110px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_cc74fbba-7d42-4e61-a0ea-eedd771fa683/artifacts/7xq5yifk_ResHubNEW1.1-removebg-preview.png"
            alt="ResiliencyHUB"
            className="h-[95px] w-auto"
          />
          <div className="hidden sm:block">
            <BrandText size="lg" className="block leading-tight" />
            <span className="text-[11px] font-medium leading-tight block mt-0.5" style={{ color: '#0e6f8e' }}>AI-Powered Resiliency Management Service</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.href.startsWith("#") ? (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-slate-600 hover:text-sky-700 transition-colors duration-200"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm font-medium text-slate-600 hover:text-sky-700 transition-colors duration-200"
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" size="sm" className="text-sm font-medium text-slate-600 hover:text-sky-700" data-testid="nav-login-btn">
              Sign In
            </Button>
          </Link>
          <Link to="/request-demo">
            <Button size="sm" className="bg-sky-700 hover:bg-sky-800 text-sm font-semibold px-5 shadow-sm" data-testid="nav-cta-btn">
              Request Enquiry &amp; Demo
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden p-2 text-slate-700"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200 px-4 pb-5 pt-2 shadow-lg">
          <div className="space-y-1 mb-4">
            {navLinks.map((link) =>
              link.href.startsWith("#") ? (
                <a
                  key={link.label}
                  href={link.href}
                  className="block py-2.5 text-sm font-medium text-slate-700 hover:text-sky-700"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block py-2.5 text-sm font-medium text-slate-700 hover:text-sky-700"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>
          <div className="flex flex-col gap-2 pt-3 border-t border-slate-200">
            <Link to="/login" onClick={() => setMobileOpen(false)}>
              <Button variant="outline" className="w-full border-slate-300 text-slate-700" size="sm">Sign In</Button>
            </Link>
            <Link to="/request-demo" onClick={() => setMobileOpen(false)}>
              <Button className="w-full bg-sky-700 hover:bg-sky-800" size="sm">
                Request Enquiry &amp; Demo
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
